import { ReactNode, useCallback, useMemo, useRef } from 'react'

import { useLogin } from '@privy-io/react-auth'
import { useConnectModal } from '@rainbow-me/rainbowkit'

import { List } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { RecoverySignerDisplay } from '~/components/create-workspace/steps/5_RecoveryStep/RecoverySignerDisplay'
import { useAuth } from '~/context/AuthProvider'
import { OrgEOASigner } from '~/types'

// Module-level storage for the modal content to restore after the signer's
// auth UI (Privy login or RainbowKit) closes. Lives outside React so it
// survives the modal unmount/remount cycle.
let pendingModalContent: ReactNode = null

export const getPendingModalContent = () => pendingModalContent
export const clearPendingModalContent = () => {
  pendingModalContent = null
}

export const useRecoverySignerModal = (eoaSigners: OrgEOASigner[]) => {
  const { login: privyLogin } = useLogin()
  const { openConnectModal } = useConnectModal()
  const { setGlobalModalState, globalModalState, closeGlobalModal } = useAuth()
  const previousContentRef = useRef(globalModalState.content)

  const recoverySignerItems = useMemo(
    () =>
      eoaSigners.map((signer) => ({
        key: signer.id,
        content: (
          <RecoverySignerDisplay
            address={signer.address}
            name={signer.name}
            email={signer.email}
          />
        ),
        onClick: () => {
          // See useRecoveryAuthRestore for why the modal closes before the
          // handoff and how it is re-opened.
          pendingModalContent = previousContentRef.current
          closeGlobalModal()
          if (signer.email) {
            privyLogin({
              loginMethods: ['email'],
              prefill: { type: 'email', value: signer.email },
            })
            return
          }
          if (openConnectModal) {
            openConnectModal()
            return
          }
          // RainbowKit refuses to open while wagmi is connected or connecting,
          // so no close would ever restore the dialog. Restore it now.
          pendingModalContent = null
          setGlobalModalState({
            isOpen: true,
            content: previousContentRef.current,
          })
        },
      })),
    [
      eoaSigners,
      closeGlobalModal,
      setGlobalModalState,
      privyLogin,
      openConnectModal,
    ],
  )

  const openRecoverySignerModal = useCallback(() => {
    previousContentRef.current = globalModalState.content
    setGlobalModalState({
      isOpen: true,
      content: (
        <SplitsDialogContent
          id="recovery-signers"
          title="Select a recovery signer"
          variant="info"
        >
          <List label="Recovery signers" items={recoverySignerItems} />
        </SplitsDialogContent>
      ),
    })
  }, [setGlobalModalState, globalModalState.content, recoverySignerItems])

  return { recoverySignerItems, openRecoverySignerModal }
}
